import type { DraftEntry, StorageAdapter } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function tryParse<T>(raw: string | null): DraftEntry<T> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DraftEntry<T>;
  } catch {
    return null;
  }
}

function tryStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

// ─── LocalStorageAdapter ──────────────────────────────────────────────────────

/**
 * Stores drafts in `window.localStorage`.
 * Drafts persist across browser sessions until they expire or are cleared.
 *
 * All access is wrapped in try/catch — localStorage can throw in:
 * - Private browsing mode (Safari, Firefox)
 * - When the storage quota is exceeded (`QuotaExceededError`)
 * - When storage access is blocked by browser permissions
 */
export class LocalStorageAdapter implements StorageAdapter {
  private readonly _namespace: string;

  /**
   * @param namespace  Prefix for all storage keys. Default: 'erp-drafts'.
   *                   Change this to isolate draft storage between app instances.
   */
  constructor(namespace = 'erp-drafts') {
    this._namespace = namespace;
  }

  private _storageKey(key: string): string {
    return `${this._namespace}:${key}`;
  }

  get<T>(key: string): DraftEntry<T> | null {
    try {
      return tryParse<T>(localStorage.getItem(this._storageKey(key)));
    } catch {
      return null;
    }
  }

  set<T>(key: string, entry: DraftEntry<T>): void {
    const serialized = tryStringify(entry);
    if (!serialized) {
      console.warn(`[DraftManager] Could not serialize draft for key "${key}".`);
      return;
    }
    try {
      localStorage.setItem(this._storageKey(key), serialized);
    } catch {
      // Quota exceeded or storage blocked — silently drop. The draft is in
      // memory (registry cache) so the current session is not affected.
      console.warn(`[DraftManager] localStorage unavailable — draft "${key}" not persisted.`);
    }
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(this._storageKey(key));
    } catch {
      /* ignore */
    }
  }

  keys(prefix?: string): string[] {
    try {
      const storagePrefix = prefix ? `${this._namespace}:${prefix}` : `${this._namespace}:`;

      return (
        Object.keys(localStorage)
          .filter((k) => k.startsWith(storagePrefix))
          // Strip the namespace prefix to return the logical draft key
          .map((k) => k.slice(this._namespace.length + 1))
      );
    } catch {
      return [];
    }
  }

  clear(prefix?: string): void {
    try {
      const storagePrefix = prefix ? `${this._namespace}:${prefix}` : `${this._namespace}:`;

      const toRemove = Object.keys(localStorage).filter((k) => k.startsWith(storagePrefix));
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }
}

// ─── SessionStorageAdapter ────────────────────────────────────────────────────

/**
 * Stores drafts in `window.sessionStorage`.
 * Drafts persist for the current browser tab session only — cleared when
 * the tab is closed. Suitable for sensitive data that should not persist.
 *
 * Shares the same error-resilient interface as `LocalStorageAdapter`.
 */
export class SessionStorageAdapter implements StorageAdapter {
  private readonly _namespace: string;

  constructor(namespace = 'erp-drafts') {
    this._namespace = namespace;
  }

  private _storageKey(key: string): string {
    return `${this._namespace}:${key}`;
  }

  get<T>(key: string): DraftEntry<T> | null {
    try {
      return tryParse<T>(sessionStorage.getItem(this._storageKey(key)));
    } catch {
      return null;
    }
  }

  set<T>(key: string, entry: DraftEntry<T>): void {
    const serialized = tryStringify(entry);
    if (!serialized) return;
    try {
      sessionStorage.setItem(this._storageKey(key), serialized);
    } catch {
      console.warn(`[DraftManager] sessionStorage unavailable — draft "${key}" not persisted.`);
    }
  }

  remove(key: string): void {
    try {
      sessionStorage.removeItem(this._storageKey(key));
    } catch {
      /* ignore */
    }
  }

  keys(prefix?: string): string[] {
    try {
      const storagePrefix = prefix ? `${this._namespace}:${prefix}` : `${this._namespace}:`;

      return Object.keys(sessionStorage)
        .filter((k) => k.startsWith(storagePrefix))
        .map((k) => k.slice(this._namespace.length + 1));
    } catch {
      return [];
    }
  }

  clear(prefix?: string): void {
    try {
      const storagePrefix = prefix ? `${this._namespace}:${prefix}` : `${this._namespace}:`;

      Object.keys(sessionStorage)
        .filter((k) => k.startsWith(storagePrefix))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }
}

// ─── MemoryStorageAdapter ─────────────────────────────────────────────────────

/**
 * Stores drafts in process memory (a plain `Map`).
 * Cleared when the page is refreshed or the component tree unmounts.
 *
 * Use cases:
 * - Unit testing (no side effects on actual browser storage)
 * - Environments where localStorage is unavailable
 * - Drafts that should only survive for the current page session
 */
export class MemoryStorageAdapter implements StorageAdapter {
  private readonly _store = new Map<string, DraftEntry<unknown>>();

  get<T>(key: string): DraftEntry<T> | null {
    return (this._store.get(key) as DraftEntry<T> | undefined) ?? null;
  }

  set<T>(key: string, entry: DraftEntry<T>): void {
    this._store.set(key, entry);
  }

  remove(key: string): void {
    this._store.delete(key);
  }

  keys(prefix?: string): string[] {
    const all = [...this._store.keys()];
    return prefix ? all.filter((k) => k.startsWith(prefix)) : all;
  }

  clear(prefix?: string): void {
    if (!prefix) {
      this._store.clear();
      return;
    }
    for (const key of [...this._store.keys()]) {
      if (key.startsWith(prefix)) this._store.delete(key);
    }
  }
}

// ─── isStorageAvailable (utility) ─────────────────────────────────────────────

/**
 * Returns true if `localStorage` (or `sessionStorage`) is available and
 * writable. Useful for feature detection before choosing an adapter.
 *
 * @example
 * const adapter = isStorageAvailable('local')
 *   ? new LocalStorageAdapter()
 *   : new MemoryStorageAdapter();
 */
export function isStorageAvailable(type: 'local' | 'session' = 'local'): boolean {
  const storage = type === 'local' ? window.localStorage : window.sessionStorage;
  const testKey = '__erp_storage_test__';
  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
